import {useState, useEffect, useRef} from 'react'
import {TextField, Checkbox, FormControlLabel, Box} from '@mui/material'
import {Button} from '@origamirisk/origami-storybook'
import {useAccount} from '../context/AccountContext'
import RatingOutputViewer from '../components/RatingOutputViewer'
import {ProposalDetailsModal} from '../components/BulkRating'
import './ProposalRating.css'

function ProposalRating() {
    const {selectedAccount} = useAccount()
    const [proposalId, setProposalId] = useState<string>('')
    const [persistRatingResponse, setPersistRatingResponse] = useState(false)
    const [includeExecutionTree, setIncludeExecutionTree] = useState(false)
    const [includeLogs, setIncludeLogs] = useState(true)
    const [isSecondPass, setIsSecondPass] = useState(true)
    const [includeTailEquations, setIncludeTailEquations] = useState(true)
    const [includeDataComparison, setIncludeDataComparison] = useState(true)
    const [includeWorksheet, setIncludeWorksheet] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [response, setResponse] = useState<any>(null)
    const [error, setError] = useState<string>('')
    const [elapsedTime, setElapsedTime] = useState<number>(0)
    const [isTimerRunning, setIsTimerRunning] = useState(false)
    const timerRef = useRef<number | null>(null)
    const startTimeRef = useRef<number>(0)
    const [selectedRecord, setSelectedRecord] = useState<any>(null)

    useEffect(() => {
        if (isTimerRunning) {
            timerRef.current = setInterval(() => {
                setElapsedTime(Date.now() - startTimeRef.current)
            }, 10)
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
        }
    }, [isTimerRunning])

    const formatTime = (ms: number): string => {
        const totalSeconds = Math.floor(ms / 1000)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        const milliseconds = ms % 1000

        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`
    }

    const handleSubmit = async () => {
        if (!proposalId) {
            setError('Proposal ID is required')
            return
        }

        if (!selectedAccount) {
            setError('Please select an account')
            return
        }

        // Reset and start timer
        setElapsedTime(0)
        startTimeRef.current = Date.now()
        setIsTimerRunning(true)

        setIsLoading(true)
        setError('')
        setResponse(null)

        try {
            const res = await fetch('/api/proposals/rate', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    proposalId: parseInt(proposalId),
                    persistRatingResponse,
                    accountName: selectedAccount,
                    includeRawRatingResponse: true,
                    lambdaOptions: {
                        includeExecutionTree,
                        includeLogs,
                        isSecondPass,
                        includeWorksheet,
                        includeTailEquations,
                        includeDataComparison
                    }
                })
            })

            if (!res.ok) {
                const errorData = await res.json()
                throw new Error(errorData.message || 'Failed to rate proposal')
            }

            const data = await res.json()
            setResponse(data)
        } catch (err: any) {
            setError(err.message || 'An error occurred')
        } finally {
            setIsLoading(false)
            setIsTimerRunning(false)
        }
    }

    return (
        <div className="proposal-rating" style={{height: 'calc(100vh - 20px)', display: 'flex', flexDirection: 'column'}}>

            <Box sx={{display: 'flex', gap: '30px', flex: 1, minHeight: 0}}>
                {/* Left side - Form */}
                <Box sx={{flex: '0 0 33.333%', display: 'flex', flexDirection: 'column', gap: 2}}>
                    <TextField
                        label="Proposal ID"
                        type="number"
                        value={proposalId}
                        onChange={(e) => setProposalId(e.target.value)}
                        fullWidth
                        required
                        disabled={isLoading}
                    />

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={persistRatingResponse}
                                    onChange={(e) => setPersistRatingResponse(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Persist Rating Response"
                        />
                    </Box>

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 5}}>
                        <Box sx={{fontWeight: 600, fontSize: '1.1rem', color: 'text.primary'}}>Rating Engine Options</Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeExecutionTree}
                                    onChange={(e) => setIncludeExecutionTree(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Include Execution Tree"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeLogs}
                                    onChange={(e) => setIncludeLogs(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Include Logs"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isSecondPass}
                                    onChange={(e) => setIsSecondPass(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Include Second Pass"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeWorksheet}
                                    onChange={(e) => setIncludeWorksheet(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Include Worksheet"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeTailEquations}
                                    onChange={(e) => setIncludeTailEquations(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Include Tail Equations"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeDataComparison}
                                    onChange={(e) => setIncludeDataComparison(e.target.checked)}
                                    disabled={isLoading}
                                />
                            }
                            label="Include Data Comparison"
                        />
                    </Box>

                    <Box sx={{display: 'flex', alignItems: 'center', gap: 3}}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isLoading || !proposalId || !selectedAccount}
                            style={{width: '120px'}}
                        >
                            {isLoading ? 'Rating...' : 'Rate Proposal'}
                        </Button>
                        {elapsedTime > 0 && (
                            <Box sx={{
                                fontFamily: '"Courier New", monospace',
                                fontSize: '20px',
                                fontWeight: 700,
                                color: isTimerRunning ? '#00ff41' : '#4caf50',
                                backgroundColor: '#1a1a1a',
                                padding: '2px 16px 0',
                                borderRadius: '4px',
                                border: '2px solid #333',
                                boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5)',
                                letterSpacing: '0.1em',
                                textShadow: isTimerRunning
                                    ? '0 0 8px rgba(0, 255, 65, 0.8), 0 0 12px rgba(0, 255, 65, 0.4)'
                                    : '0 0 8px rgba(76, 175, 80, 0.8), 0 0 12px rgba(76, 175, 80, 0.4)'
                            }}>
                                {formatTime(elapsedTime)}
                            </Box>
                        )}
                    </Box>

                    {error && (
                        <Box sx={{
                            padding: '12px',
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            borderRadius: '4px',
                            border: '1px solid #ef5350'
                        }}>
                            {error}
                        </Box>
                    )}

                    {response && (
                        <div style={{marginTop: '20px'}}>
                            <a
                                onClick={() => setSelectedRecord({
                                    proposalId: parseInt(proposalId),
                                    data: response.rawRatingPayload[0]
                                })}
                            >
                                View Proposal Details
                            </a>
                        </div>
                    )}
                </Box>

                {/* Right side - Editor */}
                <Box sx={{flex: '0 0 66.666%', display: 'flex', flexDirection: 'column', minHeight: 0}}>
                    <Box sx={{flex: 1, minHeight: 0}}>
                        <RatingOutputViewer content={response ? JSON.stringify(response, null, 2) : '// No response yet'}/>
                    </Box>
                </Box>
            </Box>

            {selectedRecord && (
                <ProposalDetailsModal
                    open={!!selectedRecord}
                    onClose={() => setSelectedRecord(null)}
                    record={selectedRecord}
                />
            )}
        </div>
    )
}

export default ProposalRating
